import { LOCATION_CHANGE } from 'react-router-redux';
import { combineReducers } from 'redux';
import _ from 'lodash';

export function items(state = [], action) {
  switch (action.type) {
    case 'ADD_SITES':
      return action.sites || [];
    case 'ADD_MORE_SITES':
      return [...state, ...action.sites];
    default:
      return state;
  }
}

export function active(state = {}, action) {
  const { demographics } = action;
  switch (action.type) {
    case 'ADD_ACTIVE_SITE':
      return action.site;
    case 'ADD_ACTIVE_SITE_DEMOGRAPHICS':
      return Object.assign({}, state, { demographics });
    case 'CLEAR_ACTIVE_SITE':
      return {};
    default:
      return state;
  }
}

const areaDefaults = {
  states: {},
  regions: {},
  counties: {},
  communities: {},
};

// sets the area layer type that is currently active, ie. state, county etc.
// ie: states: { name: 'Kansas', state_cd: 'KS', id: '20', _id: '59307fac6a0821227f7ae02e' }
export function areaActive(state = areaDefaults, { type, area, polygons, layer }) {
  switch (type) {
    case 'ADD_ACTIVE_SITE_AREA':
      return { ...state, ...area };
    case 'ADD_ACTIVE_SITE_AREA_POLYGON': {
      const newState = { ...state[layer], polygon: polygons[0] };
      return { ...state, [layer]: newState };
    }
    default:
      return state;
  }
}

const overviewDefaults = {
  details: '',
  education: {},
  labor: {},
  population: {},
  populationByAge: {},
  futurePopulationByAge: {},
  malePopulationByAge: {},
  femalePopulationByAge: {},
  race: {},
  housing: {},
  industry: {},
  income: {},
  nationalIncome: {},
};

const areaOverviewDefaults = {
  states: overviewDefaults,
  regions: overviewDefaults,
  counties: overviewDefaults,
  communities: overviewDefaults,
};

// sets the areasOverview tabs: details and overview
// layer represents state, region, county, community
export function areasOverview(state = areaOverviewDefaults, action) {
  const { type, report, details, layer } = action;
  switch (type) {
    case 'ADD_SITE_AREA_REPORT': {
      const reportState = { [layer]: { ...state[layer], ...report } };
      return { ...state, ...reportState };
    }
    case 'ADD_SITE_AREA_DETAILS': {
      const detailsState = { [layer]: { ...state[layer], details } };
      return { ...state, ...detailsState };
    }
    default:
      return state;
  }
}

export function filters(state = new Map(), action) {
  switch (action.type) {
    case 'ADD_SITES_FILTER':
      return new Map([...action.filters]);
    case 'CLEAR_SITES_FILTERS':
      return new Map();
    case 'REMOVE_SITES_FILTER':
      return new Map([...state].filter(([name]) => name !== action.filterName));
    default:
      return state;
  }
}

export function filterDrawnLayer(state = {}, action) {
  switch (action.type) {
    case 'ADD_SITES_FILTER_DRAWN_LAYER':
      return ({ ...state, ...action.layer });
    case 'REMOVE_SITES_FILTER_DRAWN_LAYER':
      return {};
    default:
      return state;
  }
}

export function filterValues(state = {}, action) {
  switch (action.type) {
    case 'ADD_SITES_FILTER_VALUES':
      return ({ ...state, ...action.values });
    default:
      return state;
  }
}

export function mapLayers(state = new Map(), action) {
  switch (action.type) {
    case 'RESET_SITES_MAP':
      return new Map();
    case LOCATION_CHANGE:
      if (!(/sites$/.test(action.payload.pathname))) {
        return new Map([...state]);
      } return state;
    case 'SET_SITES_DEMOGRAPHIC':
      if (!action.field) {
        const next = new Map([...state]);
        next.delete('demographics');
        return next;
      }
      return new Map([...state, ['demographics', action.field]]);
    case 'SET_SITES_CUSTOM_LAYER':
      if (!action.layer) {
        const next = new Map([...state]);
        next.delete('customLayer');
        return next;
      }
      return new Map([...state, ['customLayer', action.layer]]);
    case 'SET_SITES_PREDEFINED_LAYER':
      if (!action.layer) {
        const next = new Map([...state]);
        next.delete('predefinedLayer');
        return next;
      }
      return new Map([...state, ['predefinedLayer', action.layer]]);
    case 'SET_SITES_SEARCH_CUSTOM_LAYER':
      if (action.layer) {
        if (state.get('customLayer')) {
          if (_.get(action.previouslySearchedLayer, 'arcId', null)) {
            const removeIndex = state.get('customLayer').findIndex(layer => layer.arcId === _.get(action.previouslySearchedLayer, 'arcId'));
            if (removeIndex > -1) {
              state.get('customLayer').splice(removeIndex, 1);
            }
          }
          state.get('customLayer').push(action.layer);
          return new Map([...state]);
        }
        return new Map([...state, ['customLayer', [action.layer]]]);
      } else if (!action.layer && _.get(action.previouslySearchedLayer, 'arcId', null)) {
        if (state.get('customLayer')) {
          const removeIndex = state.get('customLayer').findIndex(layer => layer.arcId === _.get(action.previouslySearchedLayer, 'arcId'));
          if (removeIndex > -1) {
            state.get('customLayer').splice(removeIndex, 1);
          }
          return new Map([...state]);
        }
      }
      return state;
    default:
      return state;
  }
}
export function sortSites(state = ['acres', 'high'], action) {
  switch (action.type) {
    case 'SET_SITES_SORT':
      return action.sort;
    default:
      return state;
  }
}

export function activeTab(state = 'tab-list', action) {
  switch (action.type) {
    case 'ACTIVE_TAB':
      return action.section === 'sites'
        ? action.activeTab
        : state;
    case LOCATION_CHANGE:
      if (/sites$/.test(action.payload.pathname)) {
        return 'tab-list';
      }
      if (/sites\/.+$/.test(action.payload.pathname)) {
        return 'tab-detail';
      }
      return state;
    case 'CLOSE_FILTER':
      return 'tab-list';
    default:
      return state;
  }
}

export function pagination(state = { page: 0, count: 0 }, action) {
  switch (action.type) {
    case 'SET_SITES_PAGINATION':
      return {
        ...action.pagination,
        count: (action.pagination.count !== -1) ? action.pagination.count : state.count,
      };
    default:
      return state;
  }
}

export function businesses(state = null, action) {
  switch (action.type) {
    case 'SET_SITES_NAICS_CODE_ERROR':
      return {
        naicsCode: action.naicsCode,
        error: action.error,
      };
    case 'SET_SITES_NAICS_CODE':
      return {
        naicsCode: action.naicsCode,
        items: action.businesses,
      };
    case 'LOADING_SITES_NAICS_CODE':
      return {
        loading: true,
        naicsCode: action.naicsCode,
      };
    default:
      return state;
  }
}

export default combineReducers({
  activeTab,
  areaActive,
  areasOverview,
  items,
  active,
  filters,
  filterDrawnLayer,
  filterValues,
  sort: sortSites,
  mapLayers,
  pagination,
  businesses,
});



// WEBPACK FOOTER //
// ./src/store/reducers/sites.js